import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Flame,
  Eye,
  Crown,
  Check,
  Radio,
  Zap,
  Scale,
  Rocket,
  AlertTriangle,
  Users,
  Building2,
  Target,
  Share2,
  Sparkles,
  FileText,
  Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { apiClient } from "../App";
import { useAuth } from "../App";
import { toast } from "sonner";
import { deriveBuildVerdict, deriveRisks } from "../lib/opportunityDetailInsights";
import { buildMvpShipPrompt } from "../lib/buildMvpShipPrompt";
import { mergeMvpPromptIntoProjectNotes } from "../lib/mergeMvpPromptProjectNotes";
import RevenueChart from "../components/opportunity/RevenueChart";

const STATUS_META = {
  hot: { icon: <Flame className="w-4 h-4" />, className: "badge-peak" },
  rising: { icon: <TrendingUp className="w-4 h-4" />, className: "badge-rising" },
  peak: { icon: <Activity className="w-4 h-4" />, className: "badge-peak" },
  cooling: { icon: <TrendingDown className="w-4 h-4" />, className: "badge-declining" },
  saturated: { icon: <Minus className="w-4 h-4" />, className: "badge-saturated" },
  underrated: { icon: <Eye className="w-4 h-4" />, className: "badge-underrated" },
  emerging: { icon: <TrendingUp className="w-4 h-4" />, className: "badge-rising" },
};

const VERDICT_STYLES = {
  "Build Now": {
    border: "border-emerald-500/40",
    glow: "shadow-[0_0_40px_-12px_rgba(16,185,129,0.35)]",
    badge: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25",
    label: "Build Now",
  },
  "Validate First": {
    border: "border-amber-500/35",
    glow: "shadow-[0_0_36px_-14px_rgba(245,158,11,0.25)]",
    badge: "bg-amber-500/12 text-amber-200 border border-amber-500/20",
    label: "Validate first",
  },
  Avoid: {
    border: "border-rose-500/35",
    glow: "shadow-[0_0_36px_-14px_rgba(244,63,94,0.2)]",
    badge: "bg-rose-500/12 text-rose-200 border border-rose-500/20",
    label: "Avoid",
  },
};

function fmtUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtGrowthPct(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function safeText(value, fallback = "—") {
  if (value == null) return fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return fallback;
}

function MetricTile({ label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] backdrop-blur md:rounded-3xl md:border md:border-white/[0.08] md:p-4">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <p
        className="text-xl font-semibold text-white tabular-nums tracking-tight md:text-2xl"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {value ?? "—"}
      </p>
      {sub ? <p className="mt-2 text-xs leading-snug text-zinc-500">{sub}</p> : null}
    </div>
  );
}

function SectionCard({ eyebrow, title, icon, children, className = "" }) {
  return (
    <section className={`rounded-[1.5rem] bg-white/[0.025] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] backdrop-blur md:rounded-[1.75rem] md:border md:border-white/[0.08] md:bg-white/[0.035] md:p-7 ${className}`}>
      {(title || eyebrow) && (
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            {eyebrow ? <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{eyebrow}</p> : null}
            {title ? (
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white md:text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {icon}
                {title}
              </h2>
            ) : null}
          </div>
        </div>
      )}
      {children}
    </section>
  );
}

function InsightTile({ label, children }) {
  return (
    <div className="rounded-2xl bg-black/20 p-3 md:border md:border-white/[0.06] md:p-4">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">{label}</p>
      <div className="text-sm leading-relaxed text-zinc-200">{children}</div>
    </div>
  );
}

function ShipFromHerePanel({
  compact = false,
  isSaved,
  saving,
  launching,
  isProPlan,
  signalCount,
  onOpenPrompt,
  onCofounder,
  onSaveProject,
}) {
  const shellClass = compact
    ? "rounded-[1.25rem] bg-gradient-to-b from-white/[0.07] via-white/[0.03] to-transparent px-4 py-5 shadow-[0_20px_48px_-28px_rgba(0,0,0,0.55)] backdrop-blur-sm"
    : "rounded-[1.75rem] bg-gradient-to-b from-white/[0.07] via-white/[0.03] to-transparent px-6 py-10 shadow-[0_28px_64px_-28px_rgba(0,0,0,0.65)] backdrop-blur-sm md:px-8 md:py-12";

  return (
    <div className={shellClass}>
      <div className={`mx-auto flex flex-col items-center text-center ${compact ? "max-w-none" : "max-w-[20rem]"}`}>
        {isSaved ? (
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-300/90 ${compact ? "mb-2" : "mb-4"}`}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
            In your projects
          </span>
        ) : null}
        <h3
          className={`font-semibold leading-tight tracking-tight text-white ${compact ? "text-lg" : "text-[1.35rem] md:text-xl"}`}
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Ship from here
        </h3>
        {compact ? (
          <p className="mt-1 text-xs text-zinc-500">Copy the prompt or continue in Cofounder.</p>
        ) : (
          <p className="mt-2 text-sm leading-relaxed text-zinc-500">Two ways forward from this opportunity.</p>
        )}

        <div className={`w-full space-y-3 text-left ${compact ? "mt-4 max-w-none" : "mt-8 max-w-[17.5rem]"}`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">1 · Copy the prompt</p>
          {compact ? (
            <p className="text-[11px] leading-snug text-zinc-500">
              Detailed MVP brief for builders — clear scope &amp; safeguards, fewer wasted credits.
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-zinc-500">
              Structured, detailed MVP brief for AI app builders — clear flows, safeguards, and product scope spelled out upfront. Framing pushes
              toward a tighter, safer build so you churn less inside your generator. Paste once instead of iterating from scratch — fewer wasted
              credits.
            </p>
          )}

          <Button
            className={`w-full rounded-2xl border-0 bg-gradient-to-b from-[#A78BFA] via-[#7C3AED] to-[#5B21B6] font-semibold text-white shadow-[0_14px_40px_-6px_rgba(124,58,237,0.45)] transition hover:brightness-105 hover:shadow-[0_18px_48px_-8px_rgba(124,58,237,0.5)] disabled:opacity-60 ${
              compact ? "h-11 px-4 text-[13px] rounded-xl" : "h-14 px-8 text-[15px]"
            }`}
            onClick={onOpenPrompt}
            data-testid="mvp-prompt-btn"
          >
            <FileText className={`mr-2 opacity-95 ${compact ? "h-4 w-4" : "h-5 w-5"}`} aria-hidden />
            {isProPlan ? "Open build prompt" : "Unlock build prompt"}
            {!isProPlan ? <Crown className="ml-2 h-4 w-4 shrink-0 text-amber-200/90" aria-hidden /> : null}
          </Button>
        </div>

        <div
          className={`w-full space-y-3 border-t border-white/[0.06] text-left ${
            compact ? "mt-4 max-w-none pt-4" : "mt-8 max-w-[17.5rem] pt-8"
          }`}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">2 · Work with Cofounder</p>
          {compact ? (
            <p className="text-[11px] leading-snug text-zinc-500">
              Save to projects; guided step-by-step plan with full context in SaaS Radar.
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-zinc-500">
              Save this signal to projects and continue in Cofounder — guided, step‑by‑step planning with full opportunity context in SaaS Radar.
              You get the same priorities (clear spec, sane architecture, cautious handling of sensitive areas) without pasting fragmented notes back
              and forth across tools.
            </p>
          )}

          <Button
            variant="outline"
            className={`w-full rounded-2xl border-[#2a2a2a] bg-white/[0.04] font-medium text-white shadow-none transition hover:border-violet-500/40 hover:bg-white/[0.07] disabled:opacity-60 ${
              compact ? "h-10 px-4 text-[13px] rounded-xl" : "h-12 px-6 text-[14px]"
            }`}
            onClick={onCofounder}
            disabled={launching}
            data-testid="build-this-saas-btn"
          >
            {launching ? (
              <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white/60 border-t-transparent" aria-hidden />
            ) : (
              <Sparkles className={`mr-2 text-violet-300/95 ${compact ? "h-4 w-4" : "h-5 w-5"}`} aria-hidden />
            )}
            {isProPlan ? (compact ? "Open Cofounder" : "Open Cofounder with project") : "Unlock Cofounder"}
            {!isProPlan ? <Crown className="ml-2 h-4 w-4 shrink-0 text-amber-200/90" aria-hidden /> : null}
          </Button>
        </div>

        <div
          className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-zinc-500 ${
            compact ? "mt-3 text-[11px]" : "mt-8 text-[13px]"
          }`}
        >
          {!isSaved ? (
            <button
              type="button"
              disabled={saving}
              data-testid="save-project-btn"
              className="rounded-md px-1 py-0.5 underline-offset-4 transition-colors hover:text-zinc-200 hover:underline disabled:pointer-events-none disabled:opacity-50"
              onClick={onSaveProject}
            >
              {saving ? "Saving…" : compact ? "Save" : "Save project"}
            </button>
          ) : null}
          {compact && signalCount != null ? (
            <span className="inline-flex items-center gap-1 text-zinc-600">
              <Zap className="h-3 w-3 text-[#7C3AED]/80" aria-hidden />
              {signalCount} radar signals
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const OpportunityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState(null);
  const [mvpPromptOpen, setMvpPromptOpen] = useState(false);
  const [mvpSyncing, setMvpSyncing] = useState(false);
  const mvpSyncedForSessionRef = useRef(false);
  const debugPlanEnabled = localStorage.getItem("plan_override_enabled") === "true";
  const overridePlan = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  const hasAdminPin = Boolean((localStorage.getItem("admin_plan_pin") || "").trim());
  /** Mirror `_resolve_effective_plan`: internal admins default to full_access on API. */
  const isInternalAdmin = Boolean(user?.is_admin) || String(user?.role || "").toLowerCase() === "admin";
  const dbPlan = (user?.plan || "free").toLowerCase();
  const serverMirroredPlan = isInternalAdmin ? "full_access" : dbPlan;
  const canUsePlanOverride = debugPlanEnabled && hasAdminPin;
  const effectivePlan =
    canUsePlanOverride && ["free", "pro", "full_access"].includes(overridePlan)
      ? overridePlan
      : serverMirroredPlan;
  const isProPlan = effectivePlan === "pro" || effectivePlan === "full_access";

  useEffect(() => {
    fetchOpportunity();
    checkIfSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let alive = true;
    setIntelLoading(true);
    apiClient
      .get(`/opportunities/${id}/market-intelligence`, { timeout: 18000 })
      .then((res) => {
        if (alive) setIntel(res.data || { enabled: false });
      })
      .catch(() => {
        if (alive) setIntel({ enabled: false, reason: "fetch_error" });
      })
      .finally(() => {
        if (alive) setIntelLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const fetchOpportunity = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await apiClient.get(`/opportunities/${id}`);
      const parsed = response?.data;
      const payload = parsed?.opportunity || parsed?.data?.opportunity || parsed?.data || parsed;

      if (!payload || typeof payload !== "object") {
        throw new Error("Opportunity payload missing or invalid");
      }

      setOpportunity(payload);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 402) {
        toast.message(detail || "This opportunity is available on Pro.");
        navigate("/dashboard/settings/billing");
        return;
      }
      console.error("Error fetching opportunity:", error);
      setError(detail || error?.message || "Opportunity not found or invalid response.");
      toast.error("Opportunity not found");
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const response = await apiClient.get("/projects");
      const projects = Array.isArray(response?.data) ? response.data : [];
      setIsSaved(projects.some((project) => project?.opportunity_id === id));
    } catch (error) {
      if (error?.response?.status === 401) {
        setIsSaved(false);
        return;
      }
      console.error("checkIfSaved error:", error);
    }
  };

  const ensureProjectSaved = async () => {
    try {
      const response = await apiClient.post("/projects", {
        opportunity_id: id,
        notes: "",
      });
      setIsSaved(true);
      return response?.data;
    } catch (error) {
      const detail = error?.response?.data?.detail || "";
      if (error?.response?.status === 402) {
        toast.message(detail || "Free plan limit reached. Upgrade to Pro.");
        navigate("/dashboard/settings/billing");
        return null;
      }
      if (error?.response?.status === 400 && detail === "Project already exists") {
        setIsSaved(true);
        return { already_exists: true };
      }
      if (error?.response?.status === 401) {
        throw new Error("You must be logged in");
      }
      throw error;
    }
  };

  const syncMvpPromptToProject = useCallback(
    async (promptBody) => {
      const mergedFresh = mergeMvpPromptIntoProjectNotes(null, promptBody);
      const { data } = await apiClient.get("/projects");
      const projects = Array.isArray(data) ? data : [];
      const existing = projects.find((p) => p.opportunity_id === id);
      if (existing?.project_id) {
        const merged = mergeMvpPromptIntoProjectNotes(existing.notes, promptBody);
        await apiClient.put(`/projects/${existing.project_id}`, { notes: merged });
        return;
      }
      try {
        await apiClient.post("/projects", { opportunity_id: id, notes: mergedFresh });
      } catch (err) {
        const detail = err?.response?.data?.detail || "";
        if (err?.response?.status === 400 && detail === "Project already exists") {
          const { data: data2 } = await apiClient.get("/projects");
          const again = (Array.isArray(data2) ? data2 : []).find((p) => p.opportunity_id === id);
          if (again?.project_id) {
            const merged = mergeMvpPromptIntoProjectNotes(again.notes, promptBody);
            await apiClient.put(`/projects/${again.project_id}`, { notes: merged });
            return;
          }
        }
        throw err;
      }
    },
    [id]
  );

  const handleSaveProject = async () => {
    if (isSaved) {
      navigate("/dashboard/projects");
      return;
    }

    setSaving(true);
    try {
      await ensureProjectSaved();
      toast.success("Added to your projects!");
    } catch (error) {
      console.error("handleSaveProject error:", error);
      if (error?.message === "You must be logged in") {
        toast.error(error.message);
      } else {
        toast.error("Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const title = opportunity?.name || "SaaSRadar";
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
        return;
      }
    } catch {
      /* user cancelled share sheet */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const mvpPromptText = useMemo(() => {
    if (!opportunity) return "";
    return buildMvpShipPrompt(opportunity, { intel });
  }, [opportunity, intel]);

  useEffect(() => {
    if (!mvpPromptOpen) {
      mvpSyncedForSessionRef.current = false;
      return;
    }
    if (!mvpPromptText.trim() || intelLoading) return;
    if (mvpSyncedForSessionRef.current) return;
    mvpSyncedForSessionRef.current = true;
    let cancelled = false;
    setMvpSyncing(true);
    (async () => {
      try {
        await syncMvpPromptToProject(mvpPromptText);
        if (!cancelled) {
          setIsSaved(true);
          toast.success("Saved to your project notes");
        }
      } catch (e) {
        mvpSyncedForSessionRef.current = false;
        if (!cancelled) {
          if (e?.response?.status === 401) {
            toast.message("Sign in to auto-save this prompt to your projects.");
          } else {
            toast.error("Could not save prompt to project");
          }
        }
      } finally {
        if (!cancelled) setMvpSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mvpPromptOpen, mvpPromptText, intelLoading, syncMvpPromptToProject]);

  const handleCopyMvpPrompt = async () => {
    try {
      await navigator.clipboard.writeText(mvpPromptText);
      toast.success("Prompt copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const handleOpenMvpPrompt = () => {
    if (!isProPlan) {
      toast.message("MVP build prompt is a Pro feature. Upgrade to continue.");
      navigate("/dashboard/settings/billing");
      return;
    }
    setMvpPromptOpen(true);
  };

  const handleLaunchWithAI = async () => {
    if (effectivePlan === "free") {
      toast.message("Cofounder is a Pro feature. Upgrade to continue.");
      navigate("/dashboard/settings/billing");
      return;
    }
    setLaunching(true);
    try {
      await ensureProjectSaved();
        toast.success(isSaved ? "Opening Cofounder..." : "Saved to projects and opening Cofounder...");
      navigate(`/dashboard/ai-builder/${id}`);
    } catch (error) {
      console.error("handleLaunchWithAI error:", error);
      if (error?.message === "You must be logged in") {
        toast.error(error.message);
      } else {
        toast.error("Unable to open Cofounder");
      }
    } finally {
      setLaunching(false);
    }
  };

  const fallbackVerdict = useMemo(() => {
    if (!opportunity) return null;
    return deriveBuildVerdict(opportunity, { velocity7d: 0, volatility14d: 6, sourceBreadth7dAvg: 2 });
  }, [opportunity]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-[#111111] border border-[#1F1F1F] rounded-[2rem] p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Unable to open this opportunity</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Button
            variant="ghost"
            className="text-zinc-300 hover:text-white"
            onClick={() => navigate("/dashboard/opportunities")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Opportunities
          </Button>
        </div>
      </div>
    );
  }

  if (!opportunity) return null;

  const statusMeta = STATUS_META[opportunity.trend_status] || STATUS_META.rising;
  const risks = deriveRisks(opportunity, {});

  const intelEnabled = Boolean(intel?.enabled);
  const tm = intelEnabled && !intel?.error;
  const primary = tm ? intel.primary : null;
  const bench = intel?.benchmarks || {};
  const anchors = intel?.chart_anchors || {};
  const chartRaw = intel?.chart && typeof intel.chart === "object" ? intel.chart : {};
  const chart = { ...chartRaw, anchors: chartRaw.anchors || anchors };

  const verdictKey = "Build Now";
  const verdictLine =
    "This opportunity is ready to execute now. Start with a tight MVP and iterate fast with user feedback.";
  const riskLevel = "Low";
  const bestFit = tm && intel.best_fit ? intel.best_fit : fallbackVerdict?.bestFit || "—";

  const verdictStyle = VERDICT_STYLES[verdictKey] || VERDICT_STYLES["Validate First"];

  const whyWorks = Array.isArray(intel?.why_this_works)
    ? intel.why_this_works.map((item) => safeText(item, "")).filter(Boolean)
    : [];
  const howRep = intel?.how_to_replicate || null;

  const revenueChartEmptyCopy =
    intelLoading || intelEnabled
      ? null
      : intel?.reason === "timeout"
        ? intel.message || "Verified benchmarks took too long. Try refreshing the page."
        : intel?.reason === "fetch_error"
          ? "Could not load benchmarks (network or server). Try again."
          : intel?.error === "no_results"
            ? "No reliable verified revenue anchors were found for this opportunity yet."
          : intel?.message && intel?.reason === "missing_api_key"
            ? intel.message
            : null;

  const techStack = Array.isArray(primary?.tech_stack) ? primary.tech_stack : [];
  const opportunityName = safeText(opportunity?.name, "Untitled opportunity");
  const opportunityDescription = safeText(opportunity?.short_description, "No description available yet.");
  const initial = opportunityName.charAt(0).toUpperCase();

  return (
    <div className="mx-auto max-w-6xl px-3 py-4 md:px-8 md:py-8 lg:px-10">
      <Button
        variant="ghost"
        className="mb-5 -ml-2 rounded-full px-3 text-zinc-500 hover:bg-white/[0.04] hover:text-white md:mb-8"
        onClick={() => navigate("/dashboard/opportunities")}
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Opportunities
      </Button>

      {/* Hero: identity, metrics, revenue chart */}
      <div className="relative mb-5 overflow-hidden rounded-[1.75rem] bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.13),transparent_40%),linear-gradient(180deg,rgba(255,255,255,0.035),rgba(255,255,255,0.012))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] backdrop-blur md:mb-8 md:rounded-[2rem] md:border md:border-white/[0.08] md:p-8 md:shadow-[0_24px_80px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.05)] lg:p-10">
        <div className="flex flex-col gap-5 md:gap-7 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-1 gap-4">
            <div
              className="hidden h-16 w-16 shrink-0 items-center justify-center rounded-3xl border border-white/[0.08] bg-white/[0.06] text-xl font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] md:flex"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              aria-hidden
            >
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-4 flex flex-wrap items-center gap-2 md:mb-5">
                <span className="rounded-full border border-white/[0.08] bg-white/[0.05] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-300">
                  {safeText(opportunity.niche)}
                </span>
                <span className={`flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium ${statusMeta.className}`}>
                  {statusMeta.icon}
                  {safeText(opportunity.trend_status)}
                </span>
                {isSaved ? (
                  <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-200/90">
                    In your projects
                  </span>
                ) : null}
                <span className="flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-zinc-400">
                  <Radio className="h-3 w-3 text-[#00E676]" />
                  Verified market
                </span>
              </div>
              <h1
                className="mb-3 max-w-4xl text-3xl font-semibold leading-[1.03] tracking-[-0.04em] text-white md:mb-4 md:text-5xl lg:text-[4rem]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {opportunityName}
              </h1>
              <p className="max-w-3xl text-base leading-7 text-zinc-400 md:text-lg">{opportunityDescription}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-white/[0.1] bg-white/[0.05] px-4 text-white hover:bg-white/[0.09]"
              onClick={handleShare}
            >
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-2.5 md:mt-9 md:grid-cols-3 md:gap-3">
          <MetricTile
            label="Revenue · last 30 days"
            value={primary?.last30_days_usd != null ? fmtUsd(primary.last30_days_usd) : bench?.avg_revenue_last30_usd != null ? fmtUsd(bench.avg_revenue_last30_usd) : null}
            sub={primary?.name ? `Anchor · ${primary.name}` : "Verified peer window (USD)"}
          />
          <MetricTile
            label="MRR"
            value={primary?.mrr_usd != null ? fmtUsd(primary.mrr_usd) : bench?.avg_mrr_usd != null ? fmtUsd(bench.avg_mrr_usd) : null}
            sub={intelLoading ? "Loading…" : intelEnabled ? "Payment-verified peer" : "Benchmarks unavailable"}
          />
          <MetricTile
            label="Growth · 30d"
            value={fmtGrowthPct(primary?.growth30d_pct)}
            sub={primary?.growth_mrr_30d_pct != null ? `MRR ${fmtGrowthPct(primary.growth_mrr_30d_pct)}` : null}
          />
        </div>

        <div className="mt-6 lg:hidden">
          <ShipFromHerePanel
            compact
            isSaved={isSaved}
            saving={saving}
            launching={launching}
            isProPlan={isProPlan}
            signalCount={opportunity.signal_count ?? 0}
            onOpenPrompt={handleOpenMvpPrompt}
            onCofounder={handleLaunchWithAI}
            onSaveProject={handleSaveProject}
          />
        </div>

        <div className="mt-6 md:mt-8">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#7C3AED]" aria-hidden />
              <h2 className="text-lg font-semibold text-white md:text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Revenue trajectory
              </h2>
            </div>
          </div>
          {intelLoading ? (
            <div className="rounded-2xl border border-[#1F1F1F] bg-[#0a0a0b] p-8 text-center text-sm text-zinc-500">
              Loading verified market data…
            </div>
          ) : (
            <RevenueChart chart={chart} hasMarketData={intelEnabled} emptyCopy={revenueChartEmptyCopy} />
          )}
        </div>
      </div>

      {/* Verdict strip */}
      <span data-testid="opportunity-score" className="sr-only">
        {Math.round(opportunity.opportunity_score || 0)}
      </span>

      <div
        data-testid="market-verdict"
        className={`mb-5 rounded-[1.5rem] ${verdictStyle.glow} bg-white/[0.03] p-4 backdrop-blur md:mb-8 md:rounded-[1.75rem] md:border ${verdictStyle.border} md:p-7`}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${verdictStyle.badge}`}>
                <Scale className="w-3.5 h-3.5 mr-1.5 opacity-80" />
                Verdict
              </span>
              <span className="text-2xl font-semibold text-white md:text-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {verdictStyle.label}
              </span>
            </div>
            <p className="max-w-3xl text-sm leading-6 text-zinc-300 md:text-base">{verdictLine}</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap lg:justify-end">
            <div className="min-w-[120px] rounded-2xl bg-black/20 px-4 py-3 md:border md:border-white/[0.08]">
              <p className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Risk</p>
              <p
                className={`text-lg font-semibold ${
                  riskLevel === "Low" ? "text-emerald-400" : riskLevel === "High" ? "text-rose-400" : "text-amber-200"
                }`}
              >
                {riskLevel}
              </p>
            </div>
            <div className="min-w-[140px] rounded-2xl bg-black/20 px-4 py-3 md:border md:border-white/[0.08]">
              <p className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Best fit</p>
              <p className="text-sm font-medium text-zinc-200">{bestFit}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-5 md:mb-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5 md:space-y-6">
          {/* Market benchmarks */}
          <SectionCard eyebrow="Verified peers" title="Market benchmark" icon={<Target className="h-5 w-5 text-[#7C3AED]" />}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <MetricTile label="Average MRR" value={bench?.avg_mrr_usd != null ? fmtUsd(bench.avg_mrr_usd) : null} sub={`Sample n = ${bench?.sample_size ?? 0}`} />
              <MetricTile label="Median MRR" value={bench?.median_mrr_usd != null ? fmtUsd(bench.median_mrr_usd) : null} sub="Middle of peer MRR" />
              <MetricTile label="Top MRR" value={bench?.top_mrr_usd != null ? fmtUsd(bench.top_mrr_usd) : null} sub="Highest in filtered set" />
              <MetricTile label="Average growth · 30d" value={fmtGrowthPct(bench?.avg_growth30d_pct)} sub="Revenue growth across peers" />
              <MetricTile
                label="Average revenue · last 30d"
                value={bench?.avg_revenue_last30_usd != null ? fmtUsd(bench.avg_revenue_last30_usd) : null}
                sub="Payment-verified window"
              />
            </div>
            {!tm && !intelLoading ? (
              <p className="mt-4 text-sm text-zinc-500">{intel?.message || "Market benchmarks unavailable for this view."}</p>
            ) : null}
            {intelLoading ? <p className="mt-4 text-sm text-zinc-500">Loading market benchmarks…</p> : null}
          </SectionCard>

          {/* Why this works */}
          {whyWorks.length > 0 ? (
            <SectionCard eyebrow="Signal read" title="Why this works" icon={<Sparkles className="h-5 w-5 text-[#7C3AED]" />}>
              <ul className="grid gap-3 md:grid-cols-2">
                {whyWorks.map((line, idx) => (
                  <li key={idx} className="rounded-2xl bg-black/20 p-3 text-sm leading-6 text-zinc-300 md:border md:border-white/[0.06] md:p-4">
                    {line}
                  </li>
                ))}
              </ul>
            </SectionCard>
          ) : null}

          {/* Startup insights */}
          <SectionCard eyebrow="Opportunity brief" title="Startup insights" icon={<Building2 className="h-5 w-5 text-[#7C3AED]" />}>
            <div className="grid gap-3 md:grid-cols-2">
              {opportunity.target_audience && (
                <InsightTile label="Target audience">
                  <span className="inline-flex gap-2"><Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500" /> {safeText(opportunity.target_audience)}</span>
                </InsightTile>
              )}
              {opportunity.problem_solved && (
                <InsightTile label="Problem solved">
                  <span className="inline-flex gap-2"><Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500" /> {safeText(opportunity.problem_solved)}</span>
                </InsightTile>
              )}
              {opportunity.business_model && (
                <InsightTile label="Business model">{safeText(opportunity.business_model)}</InsightTile>
              )}
              <InsightTile label={opportunity.display_mrr_usd ? "Verified peer MRR" : "Radar estimate (MRR)"}>
                <p className="mt-1 text-base font-semibold text-white">
                  {opportunity.display_mrr_usd
                    ? fmtUsd(opportunity.display_mrr_usd)
                    : `€${(opportunity.estimated_mrr_min || 0).toLocaleString()} – €${(opportunity.estimated_mrr_max || 0).toLocaleString()} / mo`}
                </p>
                <p className="text-xs text-zinc-600 mt-2 italic">
                  {opportunity.display_mrr_usd ? "Payment-verified peer benchmark." : "Internal model — not payment-verified."}
                </p>
              </InsightTile>
              <InsightTile label="Competition">
                <span
                  className={`inline-block px-3 py-1 rounded-md text-sm font-semibold ${
                    opportunity.competition === "Low"
                      ? "bg-[#10B981]/15 text-[#34D399]"
                      : opportunity.competition === "High"
                      ? "bg-[#F43F5E]/15 text-[#FB7185]"
                      : "bg-white/[0.03] text-zinc-300"
                  }`}
                >
                  {opportunity.competition || "Medium"}
                </span>
                {opportunity.time_to_mvp && (
                  <p className="text-xs text-zinc-500 mt-2">Est. MVP: {opportunity.time_to_mvp}</p>
                )}
              </InsightTile>
              <InsightTile label="Build complexity">
                <p className="text-lg font-semibold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {(() => {
                    const d = Number(opportunity.difficulty);
                    const n = Number.isFinite(d) ? d : 5;
                    if (n <= 4) return "Low";
                    if (n <= 7) return "Medium";
                    return "High";
                  })()}
                </p>
                <p className="mt-1 text-xs text-zinc-600">Internal radar band — not a market score.</p>
              </InsightTile>
            </div>
          </SectionCard>

          {/* How to replicate */}
          {howRep ? (
            <SectionCard eyebrow="Execution" title="How to replicate" icon={<Rocket className="h-5 w-5 text-[#7C3AED]" />}>
              <div className="mb-5 grid gap-3 md:grid-cols-2">
                <InsightTile label="Timeline">{safeText(howRep.timeline)}</InsightTile>
                <InsightTile label="Difficulty">
                  <p>{safeText(howRep.difficulty)}</p>
                  <p className="mt-2 text-xs text-zinc-500">{safeText(howRep.business_model_note)}</p>
                </InsightTile>
              </div>
              <ol className="space-y-3 text-sm text-zinc-300">
                {(Array.isArray(howRep.build_steps) ? howRep.build_steps : []).map((step, idx) => (
                  <li key={idx} className="flex gap-3 rounded-2xl bg-black/20 p-3 leading-6 md:border md:border-white/[0.06] md:p-4">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-xs font-semibold text-zinc-300">
                      {idx + 1}
                    </span>
                    <span>{safeText(step)}</span>
                  </li>
                ))}
              </ol>
            </SectionCard>
          ) : null}

          {/* Tech stack (verified peer) */}
          {techStack.length ? (
            <SectionCard eyebrow="Closest peer" title="Tech stack">
              <p className="mb-4 text-xs text-zinc-500">Observed on the closest payment-verified peer (not necessarily your stack).</p>
              <div className="flex flex-wrap gap-2">
                {techStack.map((t) => {
                  const slug = typeof t === "string" ? t : t?.slug || t?.name;
                  if (!slug) return null;
                  return (
                    <span
                      key={slug}
                      className="rounded-full border border-white/[0.08] bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-zinc-200"
                    >
                      {slug}
                    </span>
                  );
                })}
              </div>
            </SectionCard>
          ) : null}

          {/* Risks */}
          <SectionCard eyebrow="Due diligence" title="Risks & red flags" icon={<AlertTriangle className="h-5 w-5 text-rose-300" />}>
            <div className="grid gap-3 md:grid-cols-2">
              <InsightTile label="Main risk">{risks.mainRisk}</InsightTile>
              <InsightTile label="Why it could fail">{risks.couldFail}</InsightTile>
            </div>
          </SectionCard>
        </div>

        {/* Sidebar — desktop only; mobile uses compact panel above grid */}
        <div className="hidden space-y-6 lg:sticky lg:top-6 lg:block lg:self-start">
          <ShipFromHerePanel
            isSaved={isSaved}
            saving={saving}
            launching={launching}
            isProPlan={isProPlan}
            onOpenPrompt={handleOpenMvpPrompt}
            onCofounder={handleLaunchWithAI}
            onSaveProject={handleSaveProject}
          />

          <div className="rounded-[1.75rem] bg-white/[0.025] px-6 py-7 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_20px_48px_-40px_rgba(0,0,0,0.6)] backdrop-blur-sm md:px-8">
            <div className="mx-auto mb-2 flex justify-center gap-2 text-[#7C3AED] opacity-90">
              <Zap className="h-4 w-4" aria-hidden />
            </div>
            <p className="text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {opportunity.signal_count ?? 0}
            </p>
            <p className="mt-1.5 text-xs text-zinc-500">Radar signals</p>
          </div>
        </div>
      </div>

      <Dialog open={mvpPromptOpen} onOpenChange={setMvpPromptOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#1F1F1F] bg-[#111111] p-5 text-white sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-left text-lg text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              MVP build prompt
            </DialogTitle>
            <DialogDescription className="text-left text-zinc-400">
              Structured MVP brief for AI app builders — detailed flows, safeguards, and scope so generations stay tighter and safer. Paste once
              instead of looping on vague specs; fewer wasted credits in your builder. Plain English, no Markdown # headings. Synced to project notes
              (or project created) once market data has finished loading.
              {intelLoading && mvpPromptOpen ? (
                <span className="mt-2 block text-zinc-400">Loading market data — auto-save runs right after.</span>
              ) : null}
              {!intelLoading && mvpSyncing ? <span className="mt-2 block text-violet-300/90">Syncing to your projects…</span> : null}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            readOnly
            value={mvpPromptText}
            className="min-h-[280px] resize-y border-[#2a2a2a] bg-black/40 font-mono text-xs leading-relaxed text-zinc-200"
            spellCheck={false}
            data-testid="mvp-prompt-textarea"
          />
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="border-[#2a2a2a] bg-white/[0.04] text-white hover:bg-white/[0.08]"
              onClick={() => setMvpPromptOpen(false)}
            >
              Close
            </Button>
            <Button type="button" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleCopyMvpPrompt}>
              <Copy className="mr-2 h-4 w-4" />
              Copy prompt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunityDetailPage;
